import { useMemo } from 'react';
import { DsDatePicker, DSTypography } from '@eiv2/design-system';
import { endOfDay, startOfDay } from 'date-fns';
import { type DateRange } from '../services/dateWindow';

export interface DateRangePickerProps {
  value: DateRange;
  onChange: (next: DateRange) => void;
  disabled?: boolean;
  className?: string;
  fromLabel?: string;
  toLabel?: string;
}

function parseIsoDate(iso: string): Date | null {
  if (!iso) return null;
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? null : date;
}

function useDateRangeFields({
  value,
  onChange,
  disabled,
  fromLabel = 'Data de início',
  toLabel = 'Data de término'
}: Omit<DateRangePickerProps, 'className'>) {
  const fromDate = useMemo(() => parseIsoDate(value.from), [value.from]);
  const toDate = useMemo(() => parseIsoDate(value.to), [value.to]);
  const invalid =
    fromDate !== null && toDate !== null && toDate.getTime() <= fromDate.getTime();

  const fromPicker = (
    <DsDatePicker
      className="min-w-0 w-full"
      disabled={disabled}
      invalid={invalid}
      label={fromLabel}
      onValueChange={(next) => {
        if (!next) return;
        onChange({ ...value, from: startOfDay(next).toISOString() });
      }}
      value={fromDate}
    />
  );

  const toPicker = (
    <DsDatePicker
      className="min-w-0 w-full"
      disabled={disabled}
      invalid={invalid}
      label={toLabel}
      onValueChange={(next) => {
        if (!next) return;
        onChange({ ...value, to: endOfDay(next).toISOString() });
      }}
      value={toDate}
    />
  );

  const error = invalid ? (
    <DSTypography as="p" className="col-span-full text-[12px] font-normal text-feedback-danger">
      O fim deve ser depois do início.
    </DSTypography>
  ) : null;

  return { fromPicker, toPicker, error, invalid };
}

/** Dois date pickers soltos para encaixar em grids de filtros. */
export function DateRangeFields({
  includeError = false,
  ...props
}: Omit<DateRangePickerProps, 'className'> & { includeError?: boolean }) {
  const { fromPicker, toPicker, error } = useDateRangeFields(props);
  return (
    <>
      {fromPicker}
      {toPicker}
      {includeError ? error : null}
    </>
  );
}

export function DateRangeValidationMessage(props: Omit<DateRangePickerProps, 'className'>) {
  const { error } = useDateRangeFields(props);
  return error;
}

export function DateRangePicker({
  value,
  onChange,
  disabled,
  className,
  fromLabel = 'Data de início',
  toLabel = 'Data de término'
}: DateRangePickerProps) {
  const { fromPicker, toPicker, error } = useDateRangeFields({
    value,
    onChange,
    disabled,
    fromLabel,
    toLabel
  });

  return (
    <div className={['flex min-w-0 flex-1 flex-col gap-1', className].filter(Boolean).join(' ')}>
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:gap-4">
        <div className="min-w-0 flex-1">{fromPicker}</div>
        <div className="min-w-0 flex-1">{toPicker}</div>
      </div>
      {error}
    </div>
  );
}
